import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../../api";
import { Spinner } from "../../components/Logo";
import { PageHeader, StatusChip } from "../../components/ui";
import { Modal, Field, ConfirmButton } from "../../components/Modal";
import { ImageUpload } from "../../components/Uploads";
import { Plus, Pencil, Trash2, Layers, Settings2 } from "lucide-react";

const empty = { title: "", description: "", objectives: "", category: "", level: "Beginner", cover_url: "", badge_url: "", status: "draft" };

export default function AdminPaths() {
  const [paths, setPaths] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/learning/paths").then((r) => setPaths(r.data.paths)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      title: p.title || "",
      description: p.description || "",
      objectives: p.objectives || "",
      category: p.category || "",
      level: p.level || "Beginner",
      cover_url: p.cover_url || "",
      badge_url: p.badge_url || "",
      status: p.status || "draft",
    });
    setEditId(p.id);
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      title: form.title,
      description: form.description || "",
      objectives: form.objectives || "",
      category: form.category || "",
      level: form.level,
      cover_url: form.cover_url || "",
      badge_url: form.badge_url || "",
      status: form.status,
    };
    try {
      if (editId) await api.patch(`/admin/learning/paths/${editId}`, payload);
      else await api.post("/admin/learning/paths", payload);
      toast.success(editId ? "Path updated" : "Path created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/learning/paths/${id}`); toast.success("Path deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-paths">
      <PageHeader overline="Content" title="Learning paths" desc="Create, edit and publish learning paths and their modules."
        actions={<button className="btn-primary" onClick={openNew} data-testid="new-path-btn"><Plus size={18} /> New path</button>} />

      {!paths ? <Spinner /> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-slate-400 uppercase text-xs tracking-wider border-b border-white/10">
                <th className="py-3 px-4">Title</th><th className="px-4">Category</th><th className="px-4">Level</th><th className="px-4">Modules</th><th className="px-4">Status</th><th className="px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paths.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`path-row-${p.id}`}>
                  <td className="py-3 px-4 font-medium">{p.title}</td>
                  <td className="px-4 text-slate-400">{p.category || "—"}</td>
                  <td className="px-4 text-slate-400">{p.level}</td>
                  <td className="px-4 font-mono">{p.module_count}</td>
                  <td className="px-4"><StatusChip status={p.status} /></td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/paths/${p.id}`} className="btn-ghost !px-2 text-[#38bdf8]" title="Manage modules" data-testid={`manage-path-${p.id}`}><Settings2 size={16} /></Link>
                      <button className="btn-ghost !px-2" title="Edit" onClick={() => openEdit(p)} data-testid={`edit-path-${p.id}`}><Pencil size={16} /></button>
                      <ConfirmButton className="btn-ghost !px-2 text-red-400" onConfirm={() => del(p.id)} testid={`delete-path-${p.id}`}><Trash2 size={16} /></ConfirmButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paths.length === 0 && <div className="p-10 text-center text-slate-500 flex flex-col items-center gap-3"><Layers size={28} /> No learning paths yet.</div>}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit learning path" : "New learning path"} size="lg">
        <div className="space-y-4">
          <Field label="Title"><input className="input" value={form.title} onChange={set("title")} data-testid="path-title" /></Field>
          <Field label="Description"><textarea className="input min-h-20" value={form.description || ""} onChange={set("description")} /></Field>
          <Field label="Objectives"><textarea className="input min-h-16" value={form.objectives || ""} onChange={set("objectives")} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><input className="input" value={form.category || ""} onChange={set("category")} placeholder="e.g. Offensive" /></Field>
            <Field label="Level">
              <select className="input" value={form.level} onChange={set("level")}>
                {["Beginner", "Intermediate", "Advanced", "Expert"].map((l) => <option key={l}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Cover image (Image dimensions should be 1040 x 208 px)">
            <ImageUpload value={form.cover_url} onChange={(url) => setForm({ ...form, cover_url: url })} />
          </Field>
          <Field label="Badge image (Badge will be awarded once learner completes the module">
            <ImageUpload value={form.badge_url} onChange={(url) => setForm({ ...form, badge_url: url })} />
          </Field>
          <Field label="Status">
            <select className="input" value={form.status} onChange={set("status")} data-testid="path-status">
              {["draft", "published", "archived"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <button className="btn-primary w-full" onClick={save} data-testid="save-path-btn">{editId ? "Update path" : "Create path"}</button>
        </div>
      </Modal>
    </div>
  );
}
